//@flow
import { Component, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';
import { FormattedMessage } from 'dibs-react-intl';
import { OverviewFavoriteItem } from './OverviewFavoriteItem';
import { PAGE_TYPE_FAVORITES } from '../constants';
import Gear from 'dibs-icons/exports/Gear';
import { logError } from '../../helpers';
import { Button } from 'dibs-elements/exports/Button';

// TODO: uncomment when folder-linking restored
//import { ConnectFolderModal } from '../components/ConnectFolderModal/ConnectFolderModal';

import styles from './OverviewFavorites.scss';
import parentStyles from '../OverviewRoot.scss';

import type { OverviewFavorites_tradeFirm as TradeFirmType } from './__generated__/OverviewFavorites_tradeFirm.graphql';
import type { PageType } from '../constants';
import type { RelayRefetchProp } from 'react-relay';

type Props = {
    tradeFirm: ?TradeFirmType,
    isEditMode: boolean,
    isTrade: boolean,
    onChangePage: (pageType: PageType) => void,
    relay?: RelayRefetchProp,
};

type State = {
    showConnectModal: boolean,
};

type DecorationProps = {
    onClick: () => void,
};

const EditGear = (props: DecorationProps) => (
    <div className={styles.connectGearWrapper} onClick={props.onClick}>
        <Gear className={styles.connectGear} />
    </div>
);

const ViewAllCta = (props: DecorationProps) => (
    <div className={styles.buttonWrapper}>
        <div className={styles.button}>
            <Button dataTn="overview-favorites-view-all" type="secondary" onClick={props.onClick}>
                <FormattedMessage
                    id="abt.designProfile.overview.favorites.viewAllCta"
                    defaultMessage="View All Favorites"
                />
            </Button>
        </div>
    </div>
);

export class OverviewFavoritesComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            showConnectModal: false,
        };
    }

    onFolderSelection = () => {
        const firmUrlLabel = getter(this.props.tradeFirm).at('publicProfile').value('urlLabel');
        const { isEditMode } = this.props;

        if (!firmUrlLabel || !this.props.relay) {
            this.setState({ showConnectModal: false });
            return;
        }

        this.props.relay.refetch(
            () => ({ firmUrlLabel, isEditMode }),
            null,
            (error: ?Error) => {
                if (error) {
                    logError(error);
                }
                this.setState({ showConnectModal: false });
            },
            { force: true }
        );
    };

    onClickConnect = (): void => {
        this.setState({ showConnectModal: true });
    };

    // TODO: uncomment when folder linking restored
    /*
    renderConnectFolderModal() {
        if (!this.state.showConnectModal) {
            return null;
        }
        const firmMembers = (getter(this.props.tradeFirm).value('members') || []).map(
            member => member && member.userId
        );
        const firmId = getter(this.props.tradeFirm).value('serviceId');
        return (
            <ConnectFolderModal
                onClose={() => this.setState({ showConnectModal: false })}
                firmMembers={firmMembers}
                firmId={firmId}
                onFolderSelection={this.onFolderSelection}
            />
        );
    }
    */

    renderConnectPrompt = () => (
        <div className={styles.connect}>
            <div className={styles.connectItem}>
                <FormattedMessage
                    id="abt.designProfile.overiew.favorites.connect.subtitle"
                    defaultMessage="Show Your Favorite 1stDibs Finds"
                />
            </div>
            <div className={styles.connectItem}>
                <FormattedMessage
                    id="abt.designProfile.overview.favorites.connect.instruction"
                    defaultMessage="Display pieces you love here by connecting to one of the folders in the Favorites section of your 1stDibs account."
                />
            </div>
            <Button
                dataTn="overview-favorites-connect"
                onClick={() => this.props.onChangePage(PAGE_TYPE_FAVORITES)}
            >
                <FormattedMessage
                    id="abt.designProfile.overview.favorites.connect.buttonLabel"
                    defaultMessage="Connect to Favorites"
                />
            </Button>
        </div>
    );

    renderTiles = (tiles: Array<Element>) => {
        const props = {
            onClick: () => {
                this.props.onChangePage(PAGE_TYPE_FAVORITES);
            },
        };

        const ComponentType = this.props.isEditMode ? EditGear : ViewAllCta;
        return (
            <Fragment>
                <div className={styles.itemRow}>
                    <div className="rowFlex">{tiles}</div>
                </div>
                <ComponentType {...props} />
            </Fragment>
        );
    };

    render() {
        const { props } = this;
        const edges =
            getter(props.tradeFirm)
                .at('publicProfile')
                .at('portfoliosInflated')
                .at(0)
                .at('items')
                .value('edges') || [];

        const tiles = edges.map((edge, index) => {
            const item = getter(edge).at('node').value('item');

            const serviceId: string = getter(item).value('serviceId') || '';
            return (
                <OverviewFavoriteItem
                    key={`overview-favorite-item-${serviceId}`}
                    index={index}
                    item={(item: ?Object)}
                    isTrade={props.isTrade}
                />
            );
        });

        let body = null;

        if (props.isEditMode && tiles.length === 0) {
            body = this.renderConnectPrompt();
        } else if (tiles.length === 0) {
            return null;
        } else {
            body = this.renderTiles(tiles);
        }
        return (
            <div className={`${parentStyles.rowWrapper} rowFlex`}>
                <div className="colSm12">
                    <div className={styles.title}>
                        <FormattedMessage
                            id="abt.designProfile.overview.favorites.title"
                            defaultMessage="Favorites"
                        />
                    </div>
                    {body}
                    {/*
                        // TODO: uncomment when folder-linking restored
                        this.renderConnectFolderModal()
                    */}
                </div>
            </div>
        );
    }
}

export const OverviewFavorites = createFragmentContainer(OverviewFavoritesComponent, {
    tradeFirm: graphql`
        fragment OverviewFavorites_tradeFirm on TradeFirmType {
            serviceId
            publicProfile {
                urlLabel
                portfoliosInflated {
                    items(first: 6) {
                        edges {
                            node {
                                item {
                                    ...OverviewFavoriteItem_item
                                    serviceId
                                    title
                                    seller {
                                        serviceId
                                    }
                                    isContemporary
                                    isCustomizable
                                    isNewAndMadeToOrder
                                    classification {
                                        categories {
                                            category {
                                                key
                                                browseUrl
                                            }
                                        }
                                    }
                                    pricing {
                                        amount {
                                            amount
                                        }
                                        netPrice {
                                            amount
                                        }
                                        salePrice {
                                            amount
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
});
